import { fetchJSON as fetchAPI } from './client';
import { EDP_FALLBACK_NAV } from '../experience/navigation';

export type NavItem = {
  id: string;
  label: string;
  path: string;
  icon?: string;
  module: string;
  group: string;
  order: number;
};

export type SearchHit = {
  id: string;
  title: string;
  description?: string;
  module: string;
  path: string;
  type: string;
  score: number;
};

export type Notification = {
  id: string;
  title: string;
  body?: string;
  module: string;
  priority: string;
  read: boolean;
  action_url?: string;
  created_at: string;
};

export type JourneyRun = {
  id: string;
  type: string;
  status: string;
  steps: { id: string; name: string; module: string; status: string }[];
  correlation_id: string;
  started_at: string;
};

export type ReadinessReport = {
  phase: string;
  overall: string;
  passed: number;
  failed: number;
  warnings: number;
  gates: { id: string; name: string; module: string; status: string; message?: string; required: boolean }[];
};

export type ReleaseConfig = {
  version: string;
  phase: string;
  build_id: string;
  environment: string;
  feature_flags: Record<string, boolean>;
};

export type DiagnosticsSummary = {
  release_phase: string;
  active_journeys: number;
  unread_notifications: number;
  nav_items: number;
  readiness_status: string;
};

const API_BASE = import.meta.env.VITE_ALPHA_API ?? '/api/v1/alpha';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const alphaApi = {
  navigation: () => fetchJSON<NavItem[]>('/navigation'),
  search: (text: string) => fetchJSON<{ hits: SearchHit[]; total: number }>('/search', {
    method: 'POST',
    body: JSON.stringify({ text }),
  }),
  notifications: (unread = false) => fetchJSON<Notification[]>(`/notifications${unread ? '?unread=true' : ''}`),
  markRead: (id: string) => fetchJSON<Notification>(`/notifications/${id}/read`, { method: 'POST' }),
  journeyDefinitions: () => fetchJSON<{ type: string; name: string; description: string }[]>('/journeys/definitions'),
  startJourney: (type: string) => fetchJSON<JourneyRun>('/journeys', {
    method: 'POST',
    body: JSON.stringify({ type }),
  }),
  listJourneys: () => fetchJSON<JourneyRun[]>('/journeys'),
  readiness: () => fetchJSON<ReadinessReport>('/readiness'),
  validateE2E: () => fetchJSON<ReadinessReport>('/readiness/validate', { method: 'POST' }),
  release: () => fetchJSON<ReleaseConfig>('/release'),
  diagnostics: () => fetchJSON<DiagnosticsSummary>('/diagnostics'),
  modules: () => fetchJSON<string[]>('/modules'),
};

export const FALLBACK_NAV: NavItem[] = EDP_FALLBACK_NAV;
